/**
 * Cliente del cuestionario: contenido, sesión y guardado por paso.
 *
 * El contenido de las preguntas viene del servidor, no de este repositorio. La
 * razón está en el motor: puntúa por posición, así que la cuarta respuesta de la
 * rueda pesa en claridad porque ocupa ese sitio. Una copia local de los
 * enunciados que se desordenara seguiría pintando el cuestionario igual y
 * movería las puntuaciones sin que nada fallara.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

// Los tipos del diagnóstico son los mismos que consume la demostración: una
// sola definición para que la vista del informe no tenga que distinguir de
// dónde viene lo que pinta.
import type { Decision, Narrative, Report, Strategic } from "./api";
import { authHeaders } from "./auth";

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      ...authHeaders(),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    let detalle: unknown;
    try {
      detalle = (await response.json()) as unknown;
    } catch {
      detalle = await response.text();
    }
    throw new ApiError(response.status, detalle);
  }
  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly detail: unknown,
  ) {
    super(`La API respondió ${status}`);
    this.name = "ApiError";
  }

  /** Qué pasos faltan, cuando el informe se rechaza por incompleto. */
  get missing(): string[] {
    const d = this.detail as { detail?: { missing?: string[] } } | undefined;
    return d?.detail?.missing ?? [];
  }
}

// ── Contenido del cuestionario ───────────────────────────────────────────────

export interface ScaleOption {
  v: number;
  l: string;
}

export interface Scale {
  min: number;
  max: number;
  options: ScaleOption[];
}

export interface Item {
  id: number;
  text: string;
}

export interface Dimension {
  key: string;
  label: string;
  items: number[];
}

export interface Instrument {
  items: Item[];
  dimensions: Dimension[];
  scale: string | null;
}

export interface WheelAxis {
  key: string;
  label: string;
  weight: number;
  color: string;
  icon: string;
  description: string;
  items: string[];
}

export interface ValueQuestion {
  key: string;
  label: string;
  hint: string;
}

export interface Palanca {
  key: string;
  shortLabel: string;
  label?: string;
  description?: string;
}

export interface Option {
  key: string;
  label: string;
}

export interface WizardContent {
  bank_id: string;
  bank_version: string;
  method_version: string;
  locale: string;
  steps: string[];
  scales: Record<string, Scale>;
  instruments: Record<string, Instrument>;
  wheel_axes: WheelAxis[];
  value_questions: ValueQuestion[];
  palancas: Palanca[];
  catalogs: {
    goals: Option[];
    horizons: Option[];
    work_status: Option[];
    work_modes: Option[];
    relocation: Option[];
    contract_types: Option[];
    criteria: Option[];
  };
}

// ── Sesión ───────────────────────────────────────────────────────────────────

export interface Progress {
  steps: string[];
  completed: string[];
  pending: string[];
  next_step: string | null;
  percent: number;
}

export interface SessionSummary {
  id: string;
  status: "draft" | "scoring" | "ready" | "failed";
  method_version: string;
  scoring_version: string;
  created_at: string;
  submitted_at: string | null;
  progress: Progress;
  has_report: boolean;
}

export interface SessionDetail extends SessionSummary {
  answers: Record<string, Record<string, unknown>>;
  derived: Record<string, unknown>;
}

export interface ReportEnvelope {
  id: string;
  session_id: string;
  kind: string;
  method_version: string;
  scoring_version: string;
  narrative_source: string;
  share_token: string;
  created_at: string;
  report: Report;
  strategic: Strategic;
  decision: Decision;
  narrative: Narrative;
}

export interface Herramienta {
  clave: string;
  nombre: string;
  para_que: string;
  formato: string;
  nivel: string;
  abierta: boolean;
}

export interface Accion {
  clave: string;
  texto: string;
  hecha: boolean;
}

export interface Estacion {
  tramo: string;
  titulo: string;
  plazo: string;
  // critero-lint-disable-next-line proposito: nombre del campo en la API; en pantalla solo se ve su contenido.
  proposito: string;
  acciones: Accion[];
  herramientas: Herramienta[];
}

export interface PlanDeTrabajo {
  nivel: string;
  estaciones: Estacion[];
}

const api = {
  content: () => request<WizardContent>("/api/v1/wizard/content"),
  createSession: () => request<SessionSummary>("/api/v1/sessions", { method: "POST" }),
  listSessions: () => request<SessionSummary[]>("/api/v1/sessions"),
  session: (id: string) => request<SessionDetail>(`/api/v1/sessions/${id}`),
  saveStep: (id: string, step: string, payload: Record<string, unknown>) =>
    request<{ step: string; payload: Record<string, unknown>; progress: Progress }>(
      `/api/v1/sessions/${id}/steps/${step}`,
      { method: "PUT", body: JSON.stringify({ payload }) },
    ),
  generateReport: (id: string) =>
    request<ReportEnvelope>(`/api/v1/sessions/${id}/report`, { method: "POST" }),
  report: (id: string) => request<ReportEnvelope>(`/api/v1/sessions/${id}/report`),
  sharedReport: (token: string) =>
    request<ReportEnvelope>(`/api/v1/reports/shared/${encodeURIComponent(token)}`),

  // critero-lint-disable-next-line viaje: es el nombre de la ruta en la API, no se ve en pantalla.
  plan: (id: string) => request<PlanDeTrabajo>(`/api/v1/sessions/${id}/viaje`),
  marcarAccion: (id: string, clave: string, hecha: boolean) =>
    request<{ clave: string; hecha: boolean }>(
      // critero-lint-disable-next-line viaje
      `/api/v1/sessions/${id}/viaje/${encodeURIComponent(clave)}`,
      { method: "PUT", body: JSON.stringify({ hecha }) },
    ),
};

export default api;

// ── Hooks ────────────────────────────────────────────────────────────────────

export function useWizardContent() {
  return useQuery({
    queryKey: ["wizard-content"],
    queryFn: api.content,
    // El contenido no cambia mientras dure la sesión del navegador. Volver a
    // pedirlo al cambiar de paso serían 40 KB por pantalla sin ganancia.
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

export function useSession(id: string | undefined) {
  return useQuery({
    queryKey: ["session", id],
    queryFn: () => api.session(id as string),
    enabled: Boolean(id),
  });
}

export type SaveState = "idle" | "saving" | "saved" | "error";

/**
 * Guardado automático de un paso, con espera antes de escribir.
 *
 * Escribe 800 ms después de la última tecla. Sin esa espera, un campo de texto
 * genera una petición por carácter; con ella, una por pausa. El estado que
 * devuelve alimenta el indicador de "guardado" — que existe porque un
 * cuestionario largo sin señal de guardado hace que la gente no se atreva a
 * cerrar la pestaña.
 */
export function useAutosave(sessionId: string | undefined, step: string) {
  const queryClient = useQueryClient();
  const [state, setState] = useState<SaveState>("idle");
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const pending = useRef<Record<string, unknown>>({});

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      api.saveStep(sessionId as string, step, payload),
    onSuccess: (data) => {
      setState("saved");
      queryClient.setQueryData<SessionDetail | undefined>(["session", sessionId], (prev) =>
        prev
          ? { ...prev, answers: { ...prev.answers, [step]: data.payload }, progress: data.progress }
          : prev,
      );
    },
    onError: () => setState("error"),
  });

  const flush = useCallback(() => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = undefined;
    const payload = pending.current;
    pending.current = {};
    if (!sessionId || Object.keys(payload).length === 0) return;
    setState("saving");
    mutation.mutate(payload);
  }, [mutation, sessionId]);

  const save = useCallback(
    (patch: Record<string, unknown>) => {
      pending.current = { ...pending.current, ...patch };
      setState("saving");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, 800);
    },
    [flush],
  );

  // Lo que quede sin escribir se manda al desmontar, que es lo que ocurre al
  // pasar de paso: sin esto se perdería lo tecleado en los últimos 800 ms.
  useEffect(() => {
    return () => {
      if (timer.current) {
        clearTimeout(timer.current);
        const payload = pending.current;
        if (sessionId && Object.keys(payload).length > 0) {
          void api.saveStep(sessionId, step, payload);
        }
      }
    };
  }, [sessionId, step]);

  return { save, flush, state };
}
